import * as THREE from "three";
import { InstanceBatch } from "./instance_batch.js";
import { randomRange } from "../../../common/common_funcs.js";
import { ResourceID } from "../../../resource_loader/resource_id.js";
import { Vfx3DGlobalAtlas } from "../textures/vfx_3d_global_atlas.js";
import { AttributeSize } from "../types/core_types.js";

export const SplashLayout = {
  aPosStartTime: AttributeSize.Vec4,       // xyz: start_pos, w: start_time
  aVelDur: AttributeSize.Vec4,             // xyz: velocity, w: duration
  aParams: AttributeSize.Vec4,             // x: gravity, y: scale, z: rotation, w: initial_dissolve
  aUvSurface: AttributeSize.Vec4,          // min_x, min_y, max_x, max_y for selected surface texture
  aUvShape: AttributeSize.Vec4,            // min_x, min_y, max_x, max_y for shape sprite
} as const;

export interface ISplashParticleOptions
{
  start_pos: THREE.Vector3;
  velocity: THREE.Vector3;
  gravity: number;
  scale: number;
  start_time: number;
  duration: number;
  surface_res_id?: ResourceID;
  initial_dissolve?: number;
  rotation?: number;
  shape_sub_index?: number;
  shape_res_id?: ResourceID;
}

export class VfxSplashBatch extends InstanceBatch<typeof SplashLayout>
{
  public static readonly MAX_PARTICLES: number = 750;
  public static readonly DEFAULT_GRAVITY: number = 14.0;

  private static readonly FLUID_PARTICLE_IDS: readonly ResourceID[] = [
    ResourceID.FluidParticle1,
    ResourceID.FluidParticle2,
    ResourceID.FluidParticle3,
  ];

  constructor()
  {
    const baseGeometry = new THREE.PlaneGeometry( 1, 1 );
    const atlas_texture = Vfx3DGlobalAtlas.getTexture();
    const noise_uv = new THREE.Vector4( 0, 0, 1, 1 );
    try
    {
      const n_rect = Vfx3DGlobalAtlas.getUVRect( ResourceID.NoisePerlinTexture );
      if ( n_rect )
      {
        noise_uv.set( n_rect.min_x, n_rect.min_y, n_rect.max_x, n_rect.max_y );
      }
    }
    catch ( _e ) {}

    const material = new THREE.ShaderMaterial( {
      uniforms: {
        uTexture: { value: atlas_texture },
        uTime:    { value: 0.0 },
        uNoiseUv: { value: noise_uv },
      },
      vertexShader: `
        uniform float uTime;
        uniform vec4 uNoiseUv;

        attribute vec4 aPosStartTime;
        attribute vec4 aVelDur;
        attribute vec4 aParams;
        attribute vec4 aUvSurface;
        attribute vec4 aUvShape;

        varying vec2 vUvSurface;
        varying vec2 vUvShape;
        varying vec2 vUvErosion;
        varying vec2 vThresholdParams;

        const float POP_DURATION = 0.1;
        const float DISSOLVE_START = 0.1;
        const float DISSOLVE_SOFTNESS = 0.1;
        const float FLOOR_Y = 0.0;

        void main()
        {
          vUvSurface = mix( aUvSurface.xy, aUvSurface.zw, uv );
          vUvShape = mix( aUvShape.xy, aUvShape.zw, uv );
          vUvErosion = mix( uNoiseUv.xy, uNoiseUv.zw, uv );

          float start_time = aPosStartTime.w;
          float duration = aVelDur.w;
          float age = max( 0.0, uTime - start_time );
          float progress = clamp( age / duration, 0.0, 1.0 );

          // Dissolve parameters & threshold calculation
          float initDissolve = clamp( aParams.w, 0.0, 1.0 );
          float advanceProgress = clamp( ( progress - DISSOLVE_START ) / ( 1.0 - DISSOLVE_START ), 0.0, 1.0 );
          float currentDissolve = mix( initDissolve, 1.0, advanceProgress );
          float threshold = currentDissolve * ( 1.0 + DISSOLVE_SOFTNESS );
          vThresholdParams = vec2( threshold - DISSOLVE_SOFTNESS, threshold );

          // Initial pop scale
          float pop = clamp( age / POP_DURATION, 0.0, 1.0 );
          float current_scale = aParams.y * ( 0.6 + 0.4 * pop );

          // Parabolic trajectory position computation GPU-side:
          float gravity = aParams.x;
          vec3 computed_pos = aPosStartTime.xyz + aVelDur.xyz * age + vec3( 0.0, -0.5 * gravity * age * age, 0.0 );
          computed_pos.y = max( computed_pos.y, FLOOR_Y );

          // Camera-facing billboard quad with tilt angle:
          float rotation = aParams.z;
          float c = cos( rotation );
          float s = sin( rotation );
          mat2 rot = mat2( c, -s, s, c );
          vec2 rotated_offset = rot * position.xy;

          vec4 mvPosition = modelViewMatrix * vec4( computed_pos, 1.0 );
          mvPosition.xy += rotated_offset * current_scale;
          gl_Position = projectionMatrix * mvPosition;
        }
      `,
      fragmentShader: `
        uniform sampler2D uTexture;

        varying vec2 vUvSurface;
        varying vec2 vUvShape;
        varying vec2 vUvErosion;
        varying vec2 vThresholdParams;

        void main()
        {
          // 1. Surface Texture RGB & Alpha from Vfx3DGlobalAtlas
          vec4 surfaceTex = texture2D( uTexture, vUvSurface );

          // 2. Shape Mask from Vfx3DGlobalAtlas (white figure on transparent background)
          vec4 shapeTex = texture2D( uTexture, vUvShape );
          float shapeMask = shapeTex.a > 0.001 ? ( shapeTex.r * shapeTex.a ) : shapeTex.r;

          // 3. Erosion Dissolve Noise from Vfx3DGlobalAtlas
          vec4 erosionTex = texture2D( uTexture, vUvErosion );
          float erosionNoise = erosionTex.r;

          float dissolveAlpha = smoothstep( vThresholdParams.x, vThresholdParams.y, erosionNoise );

          // Combine shape mask, surface alpha, and dissolve alpha
          float finalAlpha = shapeMask * surfaceTex.a * dissolveAlpha;

          gl_FragColor = vec4( surfaceTex.rgb, finalAlpha );
        }
      `,
      transparent: true,
      depthWrite: false,
      depthTest: true,
      side: THREE.FrontSide,
    } );

    super( { max_instances: VfxSplashBatch.MAX_PARTICLES, layout: SplashLayout }, baseGeometry, material );
    this.instanced_mesh.renderOrder = 21;
  }

  public spawnParticle(
    start_pos: THREE.Vector3,
    velocity: THREE.Vector3,
    gravity: number = VfxSplashBatch.DEFAULT_GRAVITY,
    scale: number = 0.6,
    start_time: number = 0.0,
    duration: number = 0.8,
    surface_res_id: ResourceID = ResourceID.JamCherryTexture,
    initial_dissolve: number = 0.0,
    rotation?: number,
    shape_sub_index?: number,
    shape_res_id?: ResourceID
  ): number
  {
    return this.spawnParticleWithOptions( {
      start_pos,
      velocity,
      gravity,
      scale,
      start_time,
      duration,
      surface_res_id,
      initial_dissolve,
      rotation,
      shape_sub_index,
      shape_res_id,
    } );
  }

  public spawnParticleWithOptions( options: ISplashParticleOptions ): number
  {
    const idx = this.allocateInstanceIndex( options.start_time, options.duration );
    if ( idx === -1 )
      return -1;

    const surface_id = options.surface_res_id ?? ResourceID.JamCherryTexture;
    const surface_uv = Vfx3DGlobalAtlas.getUVRect( surface_id ) ?? { min_x: 0, min_y: 0, max_x: 1, max_y: 1 };

    const shape_id = options.shape_res_id ?? (
      ( options.shape_sub_index !== undefined && options.shape_sub_index >= 0 )
        ? VfxSplashBatch.FLUID_PARTICLE_IDS[options.shape_sub_index % VfxSplashBatch.FLUID_PARTICLE_IDS.length]
        : VfxSplashBatch.FLUID_PARTICLE_IDS[Math.floor( Math.random() * VfxSplashBatch.FLUID_PARTICLE_IDS.length )]
    );
    const shape_uv = Vfx3DGlobalAtlas.getUVRect( shape_id ) ?? { min_x: 0, min_y: 0, max_x: 1, max_y: 1 };

    const pos_start_attr = this.geometry.getAttribute( "aPosStartTime" ).array as Float32Array;
    const vel_dur_attr   = this.geometry.getAttribute( "aVelDur" ).array as Float32Array;
    const params_attr    = this.geometry.getAttribute( "aParams" ).array as Float32Array;
    const uv_surface_attr = this.geometry.getAttribute( "aUvSurface" ).array as Float32Array;
    const uv_shape_attr   = this.geometry.getAttribute( "aUvShape" ).array as Float32Array;

    const o4 = idx * 4;

    pos_start_attr[o4]     = options.start_pos.x;
    pos_start_attr[o4 + 1] = options.start_pos.y;
    pos_start_attr[o4 + 2] = options.start_pos.z;
    pos_start_attr[o4 + 3] = options.start_time;

    vel_dur_attr[o4]     = options.velocity.x;
    vel_dur_attr[o4 + 1] = options.velocity.y;
    vel_dur_attr[o4 + 2] = options.velocity.z;
    vel_dur_attr[o4 + 3] = options.duration;

    params_attr[o4]     = options.gravity;
    params_attr[o4 + 1] = options.scale;
    params_attr[o4 + 2] = options.rotation !== undefined ? options.rotation : randomRange( 0, Math.PI * 2 );
    params_attr[o4 + 3] = options.initial_dissolve ?? 0.0;

    uv_surface_attr[o4]     = surface_uv.min_x;
    uv_surface_attr[o4 + 1] = surface_uv.min_y;
    uv_surface_attr[o4 + 2] = surface_uv.max_x;
    uv_surface_attr[o4 + 3] = surface_uv.max_y;

    uv_shape_attr[o4]     = shape_uv.min_x;
    uv_shape_attr[o4 + 1] = shape_uv.min_y;
    uv_shape_attr[o4 + 2] = shape_uv.max_x;
    uv_shape_attr[o4 + 3] = shape_uv.max_y;

    this.geometry.getAttribute( "aPosStartTime" ).needsUpdate = true;
    this.geometry.getAttribute( "aVelDur" ).needsUpdate       = true;
    this.geometry.getAttribute( "aParams" ).needsUpdate       = true;
    this.geometry.getAttribute( "aUvSurface" ).needsUpdate    = true;
    this.geometry.getAttribute( "aUvShape" ).needsUpdate      = true;

    return this.getStableId( idx );
  }

  protected override updateInstance( _progress: number, _now: number, _idx: number ): boolean
  {
    return true;
  }

  protected override onUpdate( _dt: number, now: number ): void
  {
    const mat = this.material as THREE.ShaderMaterial;
    if ( mat.uniforms.uTime )
    {
      mat.uniforms.uTime.value = now;
    }
  }

  protected override onRender(): void
  {
    const atlas_tex = Vfx3DGlobalAtlas.getTexture();
    if ( atlas_tex )
    {
      const mat = this.material as THREE.ShaderMaterial;
      if ( mat.uniforms.uTexture.value !== atlas_tex )
      {
        mat.uniforms.uTexture.value = atlas_tex;
        mat.uniformsNeedUpdate = true;
      }
      if ( mat.uniforms.uNoiseUv )
      {
        const n_rect = Vfx3DGlobalAtlas.getUVRect( ResourceID.NoisePerlinTexture );
        if ( n_rect )
        {
          ( mat.uniforms.uNoiseUv.value as THREE.Vector4 ).set( n_rect.min_x, n_rect.min_y, n_rect.max_x, n_rect.max_y );
        }
      }
    }
  }
}
